import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { events, IMAGES } from '../data/events';
import { EventCard } from '../components/EventCard';
import { EventFormat } from '../types';
const filters: Array<'All' | EventFormat> = ['All', ...Array.from(new Set(events.map(event => event.format)))];
export function EventsHome() {
  const [filter, setFilter] = useState<'All' | EventFormat>('All');
  const sorted = useMemo(() => [...events].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()), []);
  const [featured, ...rest] = sorted;
  const visible = filter === 'All' ? rest : sorted.filter(event => event.format === filter);
  return <main className="w-full bg-paper" data-id="element-400" data-mp="1fpht9vyk18p0">
      <section className="relative overflow-hidden bg-ink" data-id="element-401" data-mp="1fqj2duw0fl43">
        <img src={IMAGES.hero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" data-id="element-402" data-mp="1boeb32t1b0km" />
        <div className="relative mx-auto max-w-content px-5 py-24 sm:px-8 sm:py-32" data-id="element-403" data-mp="2fmb4vn5sru1z">
          <motion.p initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="eyebrow text-white/60" data-id="element-404" data-mp="2klnpe1974p1e">
            Human Planet Events
          </motion.p>
          <motion.h1 initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.08
        }} className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.05] text-white sm:text-6xl" data-id="element-405" data-mp="2alwu3xcta609">
            Rooms where capital and impact
            <span className="text-signal" data-id="element-406" data-mp="1rx8bxj5pqes6"> actually meet</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.16
        }} className="mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg" data-id="element-407" data-mp="1rv5kqvstqflv">
            Summits, side events, roundtables and pitch days convened by Human Planet for the people mobilising capital
            for people and planet. Every room is curated, so attendance is by review.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.24
        }} className="mt-10 flex flex-wrap items-center gap-4" data-id="element-408" data-mp="3atttfzh0hi97">
            <a href="#programme" className="inline-flex items-center gap-2 rounded-full bg-signal px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-signal-deep" data-id="element-409" data-mp="2fl8ndkqdvrl">
              Register to attend
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-410" data-mp="3pkj3uzmnetc0" />
            </a>
            <Link to="/past-events" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-medium text-white transition-colors hover:border-white/70" data-id="element-411" data-mp="2j6biy73m7mtd">
              Past events
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-paper-line bg-paper-warm" data-id="element-412" data-mp="12ob6jtdqkr7l">
        <dl className="mx-auto grid max-w-content grid-cols-2 gap-y-8 px-5 py-12 sm:px-8 md:grid-cols-4" data-id="element-413" data-mp="12n01zrcgvfff">
          {[{
          label: 'Convenings a year',
          value: '8 to 10'
        }, {
          label: 'Countries and regions',
          value: '20+'
        }, {
          label: 'Capital structured',
          value: 'USD 120m+'
        }, {
          label: 'Attendance',
          value: 'By review'
        }].map(stat => <div key={stat.label} data-id="element-414" data-mp="1mcsec32seglz">
              <dd className="font-display text-3xl font-semibold text-ink sm:text-4xl" data-id="element-415" data-mp="1qdvny7u8rger">{stat.value}</dd>
              <dt className="eyebrow mt-2 text-ink-muted" data-id="element-416" data-mp="3o3x04ii1gmye">{stat.label}</dt>
            </div>)}
        </dl>
      </section>

      <section id="programme" className="mx-auto max-w-content px-5 py-20 sm:px-8" data-id="element-417" data-mp="3s6a3x1d86asj">
        <div className="flex flex-col gap-6 border-b border-paper-line pb-8 md:flex-row md:items-end md:justify-between" data-id="element-418" data-mp="2wuaoi7ms96us">
          <div data-id="element-419" data-mp="2ssp5e4jnnwc5">
            <p className="eyebrow text-signal" data-id="element-420" data-mp="2st79togh1gv5">Upcoming events</p>
            <h2 className="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl" data-id="element-421" data-mp="36thyckub6jd5">The programme ahead</h2>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filter events by format" data-id="element-422" data-mp="31uopj5pyvgjb">
            {filters.map(option => <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={filter === option} className={['rounded-full border px-4 py-2 text-sm transition-colors', filter === option ? 'border-signal bg-signal text-white' : 'border-paper-line bg-white text-ink-muted hover:border-signal/40 hover:text-ink'].join(' ')} data-id="element-423" data-mp="295rftqztms26">
                {option}
              </button>)}
          </div>
        </div>

        {filter === 'All' && featured && <div className="mt-10" data-id="element-424" data-mp="aoz5fqp7p7dt">
            <EventCard event={featured} featured data-id="element-425" data-mp="3t1x8w7l7qicq" />
          </div>}

        {visible.length > 0 ? <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-426" data-mp="xcvdxom1ecn">
            {visible.map(event => <EventCard key={event.id} event={event} data-id="element-427" data-mp="ooi8w672vujs" />)}
          </div> : <div className="mt-10 rounded-xl border border-dashed border-paper-line bg-white px-6 py-16 text-center" data-id="element-428" data-mp="tlh1gkb3ftfv">
            <p className="font-display text-xl font-semibold text-ink" data-id="element-429" data-mp="fm8zxkrgk98i">Nothing scheduled in this format yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted" data-id="element-430" data-mp="kk0rgdo6xtc6">
              We add convenings through the year. Write to events@human-planet.com and we will let you know when the
              next one opens.
            </p>
          </div>}
      </section>

      <section className="border-y border-paper-line bg-white" data-id="element-431" data-mp="1aut50h2gjc4u">
        <div className="mx-auto grid max-w-content gap-12 px-5 py-20 sm:px-8 md:grid-cols-[1fr_1.1fr]" data-id="element-432" data-mp="15x1cplxwho04">
          <div data-id="element-433" data-mp="115j9sscbhdci">
            <p className="eyebrow text-signal" data-id="element-434" data-mp="1159m5beqvi9z">How attendance works</p>
            <h2 className="mt-3 font-display text-3xl font-semibold leading-snug text-ink" data-id="element-435" data-mp="x4plvyyvjyjm">
              Curated rooms, reviewed before a ticket is issued
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-ink-muted" data-id="element-436" data-mp="s3a1m94itw2a">
              We keep our convenings deliberately mixed, with capital on one side of the table and credible delivery on
              the other. That only works if we know who is in the room, so every registration is read by our team
              before a ticket goes out.
            </p>
          </div>
          <ol className="space-y-8" data-id="element-437" data-mp="2ckoeu2zx7nza">
            {[{
            step: '01',
            title: 'Register to attend',
            body: 'Tell us who you are, what you work on, and what you want out of the session.'
          }, {
            step: '02',
            title: 'Receive confirmation of receipt',
            body: 'An email lands immediately with your reference number and the expected timeline.'
          }, {
            step: '03',
            title: 'Human Planet reviews',
            body: 'We check fit against the room we are building, usually within two working days.'
          }, {
            step: '04',
            title: 'Your ticket arrives',
            body: 'On approval we email your ticket as a PDF attachment. Bring it to the desk with photo ID.'
          }].map(item => <li key={item.step} className="flex gap-5 border-b border-paper-line pb-6 last:border-0 last:pb-0" data-id="element-438" data-mp="vt2zc6xofhco">
                <span className="font-mono text-xs text-signal" data-id="element-439" data-mp="10r4lj0qefwsd">{item.step}</span>
                <div data-id="element-440" data-mp="cf3wte4e1jvs">
                  <h3 className="font-display text-lg font-semibold text-ink" data-id="element-441" data-mp="cgf2jjhe91pj">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-muted" data-id="element-442" data-mp="7h26q98jokat">{item.body}</p>
                </div>
              </li>)}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-content px-5 py-20 sm:px-8" data-id="element-443" data-mp="ggffezouh4uh">
        <div className="rounded-xl bg-signal px-7 py-14 text-white sm:px-14" data-id="element-444" data-mp="gf4fjayish8n">
          <p className="eyebrow text-white/70" data-id="element-445" data-mp="v40kv096zg5k">Hosting with us</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-semibold leading-snug sm:text-4xl" data-id="element-446" data-mp="q5p9um75uzlr">
            Building a session for your institution?
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/85" data-id="element-447" data-mp="1057rux3igbb6">
            We co host roundtables, side events and mainstage sessions with governments, funds, networks and corporates.
            Tell us the question you want the room to answer and we will design the convening around it.
          </p>
          <a href="mailto:events@human-planet.com" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-ink transition-colors hover:bg-paper" data-id="element-448" data-mp="2rs4fcoccou1b">
            events@human-planet.com
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-449" data-mp="2n1o1x48j58ey" />
          </a>
        </div>
      </section>
    </main>;
}